import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { getUsers, register, updateUserStatus, toggleDashboardVisibility, API_URL } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Plus, Search, UserCog, X, Loader2, Check, XCircle, Eye, Camera, Palette, LayoutDashboard } from 'lucide-react';

const userTypes = { 0: 'God', 5: 'Admin', 6: 'Operations', 1: 'Employee', 2: 'Contractor', 3: 'Sub Contractor' };
const typeColors = { 0: 'bg-red-100 text-red-700', 5: 'bg-purple-100 text-purple-700', 6: 'bg-cyan-100 text-cyan-700', 1: 'bg-blue-100 text-blue-700', 2: 'bg-orange-100 text-orange-700', 3: 'bg-slate-100 text-slate-700' };

const COLOR_PALETTE = [
  { value: '#4dbd74', label: 'Green' },
  { value: '#1E88E5', label: 'Blue' },
  { value: '#E53935', label: 'Red' },
  { value: '#8E24AA', label: 'Purple' },
  { value: '#F57C00', label: 'Orange' },
  { value: '#00ACC1', label: 'Cyan' },
];

function StaffModal({ isOpen, onClose, onSubmit, loading }) {
  const [formData, setFormData] = useState({
    username: '', password: '', confirm_password: '',
    first_name: '', last_name: '', email: '', phone: '',
    user_type: 0, contractor_business_name: '', designation: '', color_code: ''
  });

  const isContractor = formData.user_type === 2;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isContractor) {
      if (!formData.username || !formData.password) { toast.error('Username and Password are required'); return; }
      if (!formData.first_name) { toast.error('First name is required'); return; }
      if (formData.password !== formData.confirm_password) { toast.error('Passwords do not match'); return; }
    } else {
      if (!formData.contractor_business_name) { toast.error('Business name is required'); return; }
      if (!formData.phone) { toast.error('Phone number is required'); return; }
    }
    const { confirm_password, ...submitData } = formData;
    onSubmit(submitData);
  };

  const handleTypeChange = (e) => {
    const newType = parseInt(e.target.value);
    setFormData({
      username: '', password: '', confirm_password: '',
      first_name: '', last_name: '', email: '', phone: '',
      user_type: newType, contractor_business_name: '', designation: '', color_code: ''
    });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-[10px] w-full max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="create-user-modal">
        <div className="p-5 border-b flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-lg font-semibold">Create Account</h2>
          <button onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="flex gap-6 p-6">
            {/* Left: User Photo */}
            <div className="w-52 shrink-0">
              <h3 className="text-sm font-bold text-primary mb-3 flex items-center gap-1"><Camera size={14} /> User's Photo</h3>
              <div className="bg-slate-100 rounded-lg p-6 flex flex-col items-center">
                <div className="w-28 h-28 rounded-full bg-slate-200 flex items-center justify-center mb-4">
                  <UserCog size={48} className="text-slate-400" />
                </div>
                <div className="text-xs text-slate-400 text-center">Photo can be uploaded after creating the account</div>
              </div>
            </div>

            {/* Right: Form */}
            <div className="flex-1 space-y-5">
              {/* Type dropdown */}
              <div>
                <label className="block text-sm text-slate-500 mb-1">Type *</label>
                <select
                  value={formData.user_type}
                  onChange={handleTypeChange}
                  className="w-full max-w-sm px-3 py-2.5 border rounded text-sm"
                  data-testid="user-type-select"
                >
                  {Object.entries(userTypes).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              {!isContractor ? (
                /* ─── Admin / Employee / Inhouse Worker Form ─── */
                <div className="bg-slate-50 rounded-lg p-5">
                  <div className="grid grid-cols-2 gap-x-10 gap-y-5">
                    {/* Personal Info column */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold text-primary flex items-center gap-1 border-b border-primary/20 pb-1">Personal Info</h3>
                      <div>
                        <label className="block text-sm text-slate-500 mb-1">Name *</label>
                        <div className="flex gap-2">
                          <input type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="First name" className="flex-1 px-3 py-2 border rounded text-sm" data-testid="user-first-name" />
                          <input type="text" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Last name" className="flex-1 px-3 py-2 border rounded text-sm" data-testid="user-last-name" />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm text-slate-500 mb-1">Designation *</label>
                        <input type="text" value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="user-designation" />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-500 mb-1">Email (Optional)</label>
                        <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="user-email" />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-500 mb-1">Phone Number *</label>
                        <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="user-phone" />
                      </div>
                    </div>

                    {/* Account Info column */}
                    <div className="space-y-4">
                      <h3 className="text-sm font-bold text-primary flex items-center gap-1 border-b border-primary/20 pb-1">Account Info</h3>
                      <div>
                        <label className="block text-sm text-slate-500 mb-1">Username *</label>
                        <input type="text" value={formData.username} onChange={(e) => setFormData({ ...formData, username: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="user-username" />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-500 mb-1">Password *</label>
                        <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="user-password" />
                      </div>
                      <div>
                        <label className="block text-sm text-slate-500 mb-1">Confirm Password *</label>
                        <input type="password" value={formData.confirm_password} onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="user-confirm-password" />
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                /* ─── Contractor Form ─── */
                <div className="bg-slate-50 rounded-lg p-5 space-y-5">
                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Business name *</label>
                    <input type="text" value={formData.contractor_business_name} onChange={(e) => setFormData({ ...formData, contractor_business_name: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="contractor-business-name" />
                  </div>

                  <h3 className="text-sm font-bold text-primary flex items-center gap-1 border-b border-primary/20 pb-1">Contact person Info</h3>
                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Name *</label>
                    <div className="flex gap-2">
                      <input type="text" value={formData.first_name} onChange={(e) => setFormData({ ...formData, first_name: e.target.value })} placeholder="First name" className="flex-1 px-3 py-2 border rounded text-sm" data-testid="contractor-first-name" />
                      <input type="text" value={formData.last_name} onChange={(e) => setFormData({ ...formData, last_name: e.target.value })} placeholder="Last name" className="flex-1 px-3 py-2 border rounded text-sm" data-testid="contractor-last-name" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Designation *</label>
                    <input type="text" value={formData.designation} onChange={(e) => setFormData({ ...formData, designation: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="contractor-designation" />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Email (Optional)</label>
                    <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="contractor-email" />
                  </div>
                  <div>
                    <label className="block text-sm text-slate-500 mb-1">Phone Number *</label>
                    <input type="text" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} className="w-full px-3 py-2 border rounded text-sm" data-testid="contractor-phone" />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Colour Picker - Always visible */}
          <div className="px-6 pb-2">
            <label className="flex items-center gap-2 text-sm text-slate-500 mb-2">
              <Palette size={14} /> Calendar Colour
            </label>
            <div className="flex flex-wrap gap-2" data-testid="create-color-picker">
              {COLOR_PALETTE.map(c => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setFormData({ ...formData, color_code: c.value })}
                  className={`w-8 h-8 rounded-full border-2 transition-all flex items-center justify-center ${formData.color_code === c.value ? 'border-slate-800 scale-110 shadow-md' : 'border-transparent hover:scale-105'}`}
                  style={{ backgroundColor: c.value }}
                  title={c.label}
                  data-testid={`create-color-${c.label.toLowerCase()}`}
                >
                  {formData.color_code === c.value && <Check size={14} className="text-white drop-shadow" />}
                </button>
              ))}
            </div>
          </div>

          <div className="p-5 border-t flex justify-end gap-3">
            <button type="button" onClick={onClose} className="px-5 py-2 border rounded text-sm bg-slate-100 hover:bg-slate-200">Cancel</button>
            <button type="submit" disabled={loading} className="px-5 py-2 bg-cyan-500 text-white rounded text-sm hover:bg-cyan-600 flex items-center gap-2" data-testid="create-account-btn">
              {loading && <Loader2 size={14} className="animate-spin" />}Create Account
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Staff() {
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isGod = currentUser?.user_type === 0;

  const { data: users, isLoading } = useQuery({ queryKey: ['users', typeFilter], queryFn: () => getUsers({ user_type: typeFilter || undefined }).then(res => res.data) });
  const createMutation = useMutation({ mutationFn: register, onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['users'] }); setShowModal(false); toast.success('Account created successfully'); }, onError: (e) => toast.error(e.response?.data?.detail || 'Failed') });
  const toggleMutation = useMutation({ mutationFn: ({ id, is_active }) => updateUserStatus(id, { is_active }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['users'] }); toast.success('Status updated'); } });
  const dashboardMutation = useMutation({ mutationFn: ({ id, show_on_dashboard }) => toggleDashboardVisibility(id, { show_on_dashboard }), onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['users'] }); queryClient.invalidateQueries({ queryKey: ['dashboard-visible-users'] }); toast.success('Dashboard visibility updated'); } });

  const filtered = users?.filter(u => {
    const q = search.toLowerCase();
    return u.first_name?.toLowerCase().includes(q) || u.last_name?.toLowerCase().includes(q) || u.username?.toLowerCase().includes(q) || u.contractor_business_name?.toLowerCase().includes(q);
  });

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="staff-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div><h1 className="text-2xl font-bold text-slate-800 font-heading">User Accounts</h1><p className="text-slate-500 text-sm mt-1">Manage team members, contractors and access</p></div>
        {isGod && (
          <button onClick={() => setShowModal(true)} className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-sm" data-testid="add-staff-btn"><Plus size={20} />Add Account</button>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-[10px] border p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by name, username, or business..." className="w-full pl-10 pr-4 py-2 border rounded-sm" data-testid="search-staff-input" />
          </div>
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="px-4 py-2 border rounded-sm" data-testid="filter-user-type">
            <option value="">All Types</option>
            {Object.entries(userTypes).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[10px] border overflow-hidden">
        <table className="w-full" data-testid="staff-table">
          <thead className="bg-slate-50 border-b">
            <tr>
              <th className="px-6 py-4 text-left text-sm font-semibold">Name</th>
              <th className="px-6 py-4 text-left text-sm font-semibold">Username</th>
              <th className="px-6 py-4 text-left text-sm font-semibold">Type</th>
              <th className="px-6 py-4 text-left text-sm font-semibold">Designation</th>
              <th className="px-6 py-4 text-left text-sm font-semibold">Contact</th>
              <th className="px-6 py-4 text-left text-sm font-semibold">Status</th>
              <th className="px-4 py-4 text-center text-sm font-semibold">Dashboard</th>
              <th className="px-6 py-4 text-right text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {isLoading ? (
              <tr><td colSpan={8} className="px-6 py-12 text-center"><Loader2 className="animate-spin mx-auto text-primary" /></td></tr>
            ) : filtered?.length === 0 ? (
              <tr><td colSpan={8} className="px-6 py-12 text-center"><UserCog size={48} className="mx-auto text-slate-300" /><p className="mt-2 text-slate-500">No accounts found</p></td></tr>
            ) : filtered?.map((user) => (
              <tr key={user.id} className="hover:bg-orange-50/30 cursor-pointer" onClick={() => navigate(`/staff/${user.id}`)} data-testid={`staff-row-${user.id}`}>
                <td className="px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="w-9 h-9 bg-primary rounded-full flex items-center justify-center text-white text-sm font-medium flex-shrink-0 overflow-hidden">
                        {user.image_url ? (
                          <img src={`${API_URL}/api/users/${user.id}/photo`} alt="" className="w-full h-full object-cover" />
                        ) : (
                          user.first_name?.charAt(0) || 'U'
                        )}
                      </div>
                      {user.color_code && (
                        <span className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2 border-white" style={{ backgroundColor: user.color_code }} data-testid={`color-dot-${user.id}`} />
                      )}
                    </div>
                    <div>
                      <p className="font-medium text-slate-800">
                        {user.user_type === 2 ? (user.contractor_business_name || `${user.first_name} ${user.last_name}`) : `${user.first_name} ${user.last_name}`}
                      </p>
                      {user.user_type === 2 && user.first_name && <p className="text-xs text-slate-500">{user.first_name} {user.last_name}</p>}
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 text-sm text-slate-600">{user.username || <span className="text-slate-300">-</span>}</td>
                <td className="px-6 py-4"><span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${typeColors[user.user_type]}`}>{userTypes[user.user_type]}</span></td>
                <td className="px-6 py-4 text-sm text-slate-600">{user.designation || '-'}</td>
                <td className="px-6 py-4 text-sm text-slate-600">{user.email || user.phone || '-'}</td>
                <td className="px-6 py-4">
                  {user.is_active !== false ? (
                    <span className="flex items-center gap-1 text-green-600 text-sm"><Check size={14} />Active</span>
                  ) : (
                    <span className="flex items-center gap-1 text-red-600 text-sm"><XCircle size={14} />Inactive</span>
                  )}
                </td>
                <td className="px-4 py-4 text-center" onClick={(e) => e.stopPropagation()}>
                  <button
                    onClick={() => dashboardMutation.mutate({ id: user.id, show_on_dashboard: !user.show_on_dashboard })}
                    className={`w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${user.show_on_dashboard ? 'bg-[#f4931d] text-white' : 'bg-slate-100 text-slate-400 hover:bg-slate-200'}`}
                    title={user.show_on_dashboard ? 'Visible on Dashboard' : 'Hidden from Dashboard'}
                    data-testid={`dashboard-toggle-${user.id}`}
                  >
                    <LayoutDashboard size={15} />
                  </button>
                </td>
                <td className="px-6 py-4 text-right" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-end gap-2">
                    <button onClick={() => navigate(`/staff/${user.id}`)} className="p-1.5 text-slate-400 hover:text-primary hover:bg-primary-light/30 rounded" title="View Details" data-testid={`view-staff-${user.id}`}>
                      <Eye size={16} />
                    </button>
                    {isGod && (
                      <button
                        onClick={() => toggleMutation.mutate({ id: user.id, is_active: user.is_active === false })}
                        className={`text-xs px-2.5 py-1 rounded-sm border ${user.is_active !== false ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-green-600 border-green-200 hover:bg-green-50'}`}
                        data-testid={`toggle-status-${user.id}`}
                      >
                        {user.is_active !== false ? 'Deactivate' : 'Activate'}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <StaffModal isOpen={showModal} onClose={() => setShowModal(false)} onSubmit={(d) => createMutation.mutate(d)} loading={createMutation.isPending} />
    </div>
  );
}

export default Staff;
